/**
 * Dashboard base wrapper element for holding
 * header nav and modal content
 */

import React, { ReactNode, ReactChild, useMemo, useCallback } from "react"
import { DashboardModal } from "./Modal/Modal"
import { Meta } from "../../../../Common/Meta/Meta"
import { DashboardLayout, Title, TitleProps } from "dash-styled"
import { connect, useDispatch } from "react-redux"
import { State } from "../../../../../Reducers"
import { ModalView } from "../../../../../Models/Client/Modal"
import { Menu } from "./Menu"
import { Helmet } from "react-helmet-async"
import { Breadcrumbs } from "../../Common/Breadcrumbs/Breadcrumbs"
import { NavPrompt } from "../../../../Common/NavPrompt/NavPrompt"
import { push } from "connected-react-router"
import { useAppState } from "../../../../../Hooks/useReduxState"
import styles from "./DashboardBase.module.css"

export interface DashboardBaseProps {
    orgUsername: string
    id?: string
    title: ReactNode
    titleComponent?: TitleProps["component"]
    description?: string
    breadcrumbs: string[]
    sidebar?: ReactChild
    banner?: ReactChild
    children: ReactNode
    modalView?: ReactChild
    fixedContentWidth: boolean
    secondaryMenuContent?: ReactNode
}

interface StateProps {
    modal?: ModalView
}

type Props = DashboardBaseProps & StateProps

const DashboardBaseIsolated = ({
    orgUsername,
    id,
    title,
    titleComponent = "h2",
    description,
    breadcrumbs,
    fixedContentWidth,
    sidebar,
    banner,
    children,
    modal,
    modalView,
    secondaryMenuContent
}: Props) => {
    const dispatch = useDispatch()
    const navigate = useCallback(path => dispatch(push(path)), [dispatch])
    const hasDirtyForm = useAppState(
        state => state.client.inputRender.hasDirtyForm
    )
    const isInModalRoute = useAppState(
        state => state.client.routeInfo.isInModalRoute
    )
    const shouldBlock = useCallback(() => hasDirtyForm, [hasDirtyForm])
    const menu = useMemo(
        () => <Menu orgUsername={orgUsername} />,
        [orgUsername]
    )
    const hasDashboardModal = modal !== undefined && modal.type !== undefined
    const main =
        !hasDashboardModal && modalView && isInModalRoute ? (
            modalView
        ) : (
            <DashboardLayout
                menuContent={menu}
                secondaryMenuContent={secondaryMenuContent}
            >
                <div className="flex h-full text-sm items-stretch">
                    <div
                        className={`flex-grow overflow-auto p-6 pb-0 flex flex-col items-stretch ${styles.DashboardBase__MainContent}`}
                    >
                        {banner}
                        <Breadcrumbs elements={breadcrumbs} />
                        <Title
                            component={titleComponent}
                            variant="heading1"
                            weight="semibold"
                            className="flex-shrink-0"
                            testId="dashboard-page-title"
                        >
                            {title}
                        </Title>
                        {description !== undefined && (
                            <p className="text-smoke-610 -mt-3 mb-3">
                                {description}
                            </p>
                        )}
                        {fixedContentWidth ? (
                            <div className="mx-auto max-w-page w-full">
                                {children}
                            </div>
                        ) : (
                            children
                        )}
                    </div>
                    {sidebar && (
                        <div className="w-64 p-4 pt-10 flex-shrink-0 h-full overflow-auto border-0 border-l border-gray-300 border-solid">
                            {sidebar}
                        </div>
                    )}
                </div>
                {!hasDashboardModal && !isInModalRoute ? (
                    modalView
                ) : (
                    <DashboardModal id={id} />
                )}
            </DashboardLayout>
        )

    return (
        <>
            <Meta title="Dashboard" description="Explore your data." />
            <Helmet>
                {/** this makes scroll bouncing look nicer */}
                <style>{`html {background-color: #000;}`}</style>
            </Helmet>
            {main}
            <NavPrompt
                navigate={navigate}
                shouldBlockNavigation={shouldBlock}
            />
        </>
    )
}

const mapStateToProps = (state: State): StateProps => {
    const { view } = state.client.modal

    return {
        modal: view === undefined ? undefined : view
    }
}

export const DashboardBase = connect(
    mapStateToProps,
    null
)(DashboardBaseIsolated)
